import constants from '../../qti/Constants';
import interactionParserUtils from '../../qti/parser/question/interactions/InteractionParserUtils';
import i18nService from '../i18nService';
import postMessageService from '../PostMessageService';
import pciParser from '../../qti/parser/question/interactions/PCIParser';

const pciTemplateVariablesCache = new WeakMap();

const pciService = {
    /**
     * Parses a stimulus/question's templateDeclarations into the PCI templateVariables format, caching the
     * result per templateDeclarations array reference so callers can share the parsed value.
     * @param templateDeclarations
     * @returns {object | undefined}
     */
    getPciTemplateVariables(templateDeclarations) {
        if (!Array.isArray(templateDeclarations)) return undefined;
        if (pciTemplateVariablesCache.has(templateDeclarations)) {
            return pciTemplateVariablesCache.get(templateDeclarations);
        }

        const templateVariables = templateDeclarations.reduce((acc, declaration) => {
            if (declaration?.identifier && declaration?.baseType && declaration?.cardinality) {
                acc[declaration.identifier] = pciParser.parseBaseTypedValue({
                    ...declaration,
                    defaultValue: declaration.value || declaration.defaultValue || [],
                });
            }
            return acc;
        }, {});
        const parsedTemplateVariables = Object.keys(templateVariables).length > 0 ? templateVariables : undefined;
        pciTemplateVariablesCache.set(templateDeclarations, parsedTemplateVariables);

        return parsedTemplateVariables;
    },

    /**
     * The object "boundTo" is compliant with the format in the PCI convention, see link: http://bit.ly/2ogZj7T
     * The property is used to indicate to the PCI interaction that the properties and templateVariables (which we use
     * for initializing) are all connected to a specific response declaration which expects an answer of a certain nature
     * (cardinality and baseType)
     * If we have provided correctResponses, then we will inject these values into the bountTo object so that we can
     * transmit it to the PCI blackbox and the interaction can use it, for example, to show a better "feedback view"
     *
     * @returns a convention-compliant boundTo object.
     */
    getBoundTo(correctResponses, boundTo) {
        if (correctResponses) {
            const identifier = Object.keys(boundTo)[0];
            const originalFromIdentifier = boundTo[identifier];
            // the only key within is the simple cardinality ('base' or 'list')
            const simpleCardinality = Object.keys(originalFromIdentifier)[0];
            // the only key has to be the interaction's baseType
            const baseType = Object.keys(originalFromIdentifier[simpleCardinality])[0];
            const valueAsArray = correctResponses.map((correctValue) => {
                return interactionParserUtils.parseValueBasedOnBaseType(baseType, correctValue);
            });
            let value = valueAsArray;

            // If this the simple cardinality is base, then we should use the first value, if any.
            if (simpleCardinality === constants.pciCardinality.BASE) {
                value = valueAsArray.length ? valueAsArray[0] : null;
            }

            const newForIdentifier = {
                [simpleCardinality]: {
                    [baseType]: value,
                },
            };

            return {
                [identifier]: newForIdentifier,
            };
        }
        return boundTo;
    },

    getResumeState(extraResponseDeclarations, extraResponses) {
        if (extraResponses.length &&
            extraResponses[0] &&
            extraResponses[0].RESPONSE_state &&
            extraResponses[0].RESPONSE_state.length) {
            return extraResponses[0].RESPONSE_state[0];
        }
        return extraResponseDeclarations[0].defaultValue.length ? extraResponseDeclarations[0].defaultValue[0] : null;
    },

    /**
     * Returns the string representation of an object that holds the mapping of the translation for the value(s) of each
     * TemplateVariable, if available. The PCI can recreate the JSON from it and use this map for any captioning it might need.
     * The format is:
     * {
     *   templateVariableName: translation | [translation, ...]
     * }
     * For a TemplateVariable's value to be translatable, it should comply with a certain regex format as well as having
     * a caption registered for it.
     *
     * @param _i18nService We send it as a parameter in order to ease mocking in unit testing.
     * @param templateVariables
     * @param questionModel
     */
    resolveCaptions(_i18nService, templateVariables, questionModel) {
        // if the value is to be translated into a caption, then it should at least match this pattern.
        const regex = new RegExp('^[_a-zA-Z][_a-zA-Z0-9]*$');
        const captions = {};
        const logicalName = questionModel.pciTemplateContentItemLogicalName ? questionModel.pciTemplateContentItemLogicalName : 'pci';
        const prefix = `interactions.pci.${logicalName}.`;

        Object.keys(templateVariables).forEach((tvName) => {
            const tv = templateVariables[tvName];
            const cardinalityScope = tv[Object.keys(tv)[0]];
            const values = cardinalityScope[Object.keys(cardinalityScope)[0]];

            if (Array.isArray(values)) {
                // if there is at least one.
                const usesCaptions = values.find((value) => {
                    return (typeof value === 'string') && regex.test(value) && _i18nService.exists(prefix + value);
                });

                if (usesCaptions) {
                    // translate as many as we can
                    captions[tvName] = values.map((value) => {
                        if ((typeof value === 'string') && regex.test(value) && _i18nService.exists(prefix + value)) {
                            return _i18nService.translate(prefix + value);
                        }
                        return null;
                    });
                }
            } else if ((typeof values === 'string') && regex.test(values) && _i18nService.exists(prefix + values)) {
                // if this value has a caption for it.
                captions[tvName] = _i18nService.translate(prefix + values);
            }
        });

        return JSON.stringify(captions);
    },

    /**
     * Resolves whether the question was answered correctly. If student hasn't submitted their answer yet, then the function
     * will return undefined as it cannot be evaluated yet.
     * @param props
     * @returns {boolean | undefined}
     */
    isCorrectOutcome(props) {
        return props.isReadOnly ? props.outcomeIsCorrect === 'true' : undefined;
    },

    getProperties(properties, templateVariables, questionModel, outcomeIsCorrect, isPreviewMode) {
        return Object.assign({}, properties, {
            lang: questionModel.language,
            captions: this.resolveCaptions(i18nService, templateVariables, questionModel),
            outcomeIsCorrect: outcomeIsCorrect ? 'true' : '',
            isPreviewMode,
        });
    },

    getStatus(props) {
        return props.isReadOnly ? constants.itemSessionStatuses.REVIEW : constants.itemSessionStatuses.INITIAL;
    },

    shouldRerenderSandbox(props, nextProps) {
        const isReset = !!nextProps.userResponse && !nextProps.userResponse.length && !!props.userResponse.length;
        const isReadOnlyTransition = nextProps.isReadOnly !== props.isReadOnly;

        if (isReset || isReadOnlyTransition) {
            return true;
        }
        return false;
    },

    getKeyboardHelpTextCaptionIfAvailable(questionModel) {
        const suffix = '.accessibilityHelperText';
        if (questionModel.pciTemplateContentItemLogicalName && i18nService.exists(questionModel.pciTemplateContentItemLogicalName + suffix)) {
            return questionModel.pciTemplateContentItemLogicalName + suffix;
        }
        return null;
    },

    getPCIConfiguration(questionModel) {
        return Object.assign({}, questionModel.pciModuleConfiguration, {
            doNotAppendExtIfProtocolIsPresent: true,
        });
    },

    writeSandboxContent(sandBox, content, onload) {
        // Putting it before the open/write/close, otherwise Safari does not consistently call onload.
        // Also, it does not work if we use arrow function.
        sandBox.onload = onload;

        sandBox.contentWindow.document.open();
        sandBox.contentWindow.document.write(content);
        sandBox.contentWindow.document.close();
    },

    postMessageToWindow(sandBox, interactionUID, message) {
        postMessageService.postTo(sandBox.contentWindow, { ...message, interactionUID });
    },

    /**
     * Given a parsed value (i.e. a value compliant with a baseType format), we translate it into the string version of it
     * Example: point [1,2] would be string "1 2".
     * @param baseType
     * @param parsedValue
     * @returns {string | null}
     */
    stringifyValueBasedOnBaseType(baseType, parsedValue) {
        let value = null;
        if ([constants.baseTypes.BOOLEAN,
            constants.baseTypes.INTEGER,
            constants.baseTypes.FLOAT,
            constants.baseTypes.INT_OR_IDENTIFIER,
            constants.baseTypes.STRING,
            constants.baseTypes.DURATION,
            constants.baseTypes.URI,
            constants.baseTypes.IDENTIFIER].indexOf(baseType) > -1) {
            value = `${parsedValue}`;
        } else if ([constants.baseTypes.POINT, constants.baseTypes.PAIR, constants.baseTypes.DIRECTED_PAIR].indexOf(baseType) > -1) {
            value = `${parsedValue[0]} ${parsedValue[1]}`;
        }
        // not supporting baseType=file yet.
        return value;
    },

    /**
     * Produces the parameters expected by ResponseService.handleUserResponse based on the information retrieved from
     * the PCI.
     * @param model
     * @param response An object compliant with the PCI representation of values.
     * @param state A string
     * @returns {interactionIdentifier, userResponses, extraResponses}
     */
    produceCallbackParameters(model, response, state) {
        if (response[model.simpleCardinality]) {
            const value = response[model.simpleCardinality][model.baseType];
            let valueAsList = null;
            if (value !== null) {
                valueAsList = (model.simpleCardinality === constants.pciCardinality.BASE ? [value] : value).map((parsedValue) => {
                    return this.stringifyValueBasedOnBaseType(model.baseType, parsedValue);
                });
            }

            return {
                interactionIdentifier: model.identifier,
                userResponses: valueAsList,
                extraResponses: [Object.assign({}, model.extraResponseDeclarations[0], {
                    candidateResponse: [state],
                })],
            };
        }
        return null;
    },

    buildSandboxHTML(interactionUID, cssPath, libs, language, body) {
        const linkTag = (cssPath ? `<link rel="stylesheet" type="text/css" href="${cssPath}" >` : '');
        const bodyHtml = body || '<body />';
        const content = `
<!DOCTYPE html>
<html lang="${language}">
<head>
    <meta charSet="UTF-8" />
    <meta name="viewport" content="width=device-width, minimum-scale=1.0" />
    <title>${interactionUID}</title>
    ${linkTag}
    ${libs.map((lib) => { return `<script src="${lib}"></script>`; }).join('')}
    <style>
        body {
          width: 100%;
          margin: 0;
          padding: 0;
        }
    </style>
</head>
${bodyHtml}
</html>`;
        return content;
    },
};

export default pciService;
